import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Check, XCircle, PartyPopper } from "lucide-react";
import { Button } from "@/components/ui/button";

const MAX_POLLS = 10;
const POLL_MS = 2000;

export default function PaymentSuccess() {
  const location = useLocation();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "polling", paymentStatus: "pending", lookupKey: null });
  const attempts = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState({ status: "error", message: "Missing session id" });
      return;
    }

    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setState({ status: "paid", lookupKey: data.lookup_key });
          return;
        }
        if (["failed", "expired", "refunded"].includes(data.payment_status)) {
          setState({ status: "failed", paymentStatus: data.payment_status });
          return;
        }
        attempts.current += 1;
        if (attempts.current >= MAX_POLLS) {
          setState({ status: "timeout" });
          return;
        }
        timer = setTimeout(poll, POLL_MS);
      } catch (e) {
        setState({ status: "error", message: e.response?.data?.detail || "Could not verify payment" });
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [location.search, refresh]);

  return (
    <div className="min-h-screen grain flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="max-w-md w-full bg-card border rounded-2xl p-8 text-center space-y-4">
        {state.status === "polling" && (
          <>
            <Loader2 className="w-10 h-10 text-terracotta mx-auto animate-spin" strokeWidth={1.5} />
            <h1 className="font-display text-2xl tracking-tight">Confirming your payment&hellip;</h1>
            <p className="text-sm text-muted-foreground">This usually takes just a couple of seconds.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <div className="w-14 h-14 rounded-full bg-forest/10 text-forest mx-auto flex items-center justify-center">
              <PartyPopper className="w-7 h-7" strokeWidth={1.75} />
            </div>
            <h1 className="font-display text-3xl tracking-tight" data-testid="payment-success-heading">
              Welcome to Pro
            </h1>
            <p className="text-sm text-muted-foreground">
              {state.lookupKey === "lingo_lifetime"
                ? "You're a Lifetime member. Every language, every day, forever."
                : "Your monthly Pro is active. Every language is unlocked."}
            </p>
            <Button
              onClick={() => nav("/dashboard")}
              className="rounded-full h-11 px-6 bg-terracotta hover:bg-terracotta/90 text-white w-full"
              data-testid="go-to-dashboard-button"
            >
              <Check className="w-4 h-4 mr-2" strokeWidth={2} /> Open my calendar
            </Button>
          </>
        )}
        {(state.status === "failed" || state.status === "error" || state.status === "timeout") && (
          <>
            <div className="w-14 h-14 rounded-full bg-destructive/10 text-destructive mx-auto flex items-center justify-center">
              <XCircle className="w-7 h-7" strokeWidth={1.75} />
            </div>
            <h1 className="font-display text-2xl tracking-tight">
              {state.status === "timeout" ? "Still waiting on Stripe" : "Payment didn't go through"}
            </h1>
            <p className="text-sm text-muted-foreground">
              {state.message || "You haven't been charged. You can retry from the pricing page."}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => nav("/dashboard")} className="rounded-full h-11 flex-1">
                Back to calendar
              </Button>
              <Button onClick={() => nav("/pricing")} className="rounded-full h-11 flex-1 bg-terracotta hover:bg-terracotta/90 text-white">
                Try again
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
